/**
 * Skeleton loaders for every data-loading surface in the R2F website.
 *
 * Design principles:
 *  - Each skeleton matches the EXACT layout of its real component (same gaps,
 *    widths, heights, grid structure).
 *  - `animate-pulse` + `bg-gray-200 rounded` for all placeholder blocks.
 *  - Round spinner fallback ONLY where layout is truly unpredictable.
 *  - Every export is a named function so tree-shaking works.
 */

/* ------------------------------------------------------------------ */
/*  1. ProductCardSkeleton  — matches ProductCard.tsx                  */
/* ------------------------------------------------------------------ */

export function ProductCardSkeleton() {
  return (
    <div
      className="flex gap-4 rounded-lg border border-gray-200 bg-white p-3 sm:p-4"
      aria-hidden="true"
    >
      {/* Left column: image only */}
      <div className="flex w-28 shrink-0 flex-col items-center sm:w-32">
        <div className="h-28 w-28 animate-pulse rounded bg-gray-200 sm:h-32 sm:w-32" />
      </div>

      {/* Right column: brand·mpn, title, rating, price+stock, stores */}
      <div className="flex flex-1 flex-col">
        {/* Brand · MPN */}
        <div className="h-3 w-40 animate-pulse rounded bg-gray-200" />
        {/* Title — two lines */}
        <div className="mt-1.5 h-4 w-11/12 animate-pulse rounded bg-gray-200" />
        <div className="mt-1 h-4 w-2/3 animate-pulse rounded bg-gray-200" />
        {/* Rating row: stars + number + review count */}
        <div className="mt-2 flex items-center gap-1.5">
          <div className="flex gap-0.5">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-4 w-4 animate-pulse rounded bg-gray-200" />
            ))}
          </div>
          <div className="h-4 w-7 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-20 animate-pulse rounded bg-gray-200" />
        </div>
        {/* Price + stock badge */}
        <div className="mt-2 flex items-center gap-3">
          <div className="h-6 w-24 animate-pulse rounded bg-gray-200" />
          <div className="h-5 w-20 animate-pulse rounded-full bg-gray-200" />
        </div>
        {/* Stores line */}
        <div className="mt-1.5 h-3 w-32 animate-pulse rounded bg-gray-200" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  2. ProductListSkeleton — initial page load (6 cards + result bar) */
/* ------------------------------------------------------------------ */

export function ProductListSkeleton() {
  return (
    <div aria-hidden="true">
      {/* "Result: X" bar */}
      <div className="mb-4 flex items-center gap-2">
        <div className="h-4 w-14 animate-pulse rounded bg-gray-200" />
        <div className="h-4 w-12 animate-pulse rounded bg-gray-200" />
      </div>

      <div className="flex flex-col gap-3">
        {Array.from({ length: 6 }, (_, i) => (
          <ProductCardSkeleton key={i} />
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  3. InfiniteScrollSkeleton — 2 cards shown at bottom while loading */
/* ------------------------------------------------------------------ */

export function InfiniteScrollSkeleton() {
  return (
    <div className="flex flex-col gap-3 pt-3" aria-hidden="true">
      <ProductCardSkeleton />
      <ProductCardSkeleton />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  4. FilterSidebarSkeleton — FilterNav (110px) + FilterPanel (220px)*/
/* ------------------------------------------------------------------ */

export function FilterSidebarSkeleton() {
  return (
    <div
      className="sticky top-[70px] flex h-[calc(100vh-70px)]"
      aria-hidden="true"
    >
      {/* FilterNav — 110px, 10 rows */}
      <nav className="flex w-[110px] shrink-0 flex-col border-r border-gray-200 bg-white">
        {Array.from({ length: 10 }, (_, i) => (
          <div
            key={i}
            className="flex items-center justify-center border-b border-gray-200 py-3"
          >
            <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
          </div>
        ))}
      </nav>

      {/* FilterPanel — 220px, header + 8 filter items */}
      <div className="w-[220px] shrink-0 border-r border-gray-200 px-3 py-3">
        {/* Section header */}
        <div className="mb-3 flex items-center justify-between">
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-10 animate-pulse rounded bg-gray-200" />
        </div>
        {/* Filter items (checkbox rows) */}
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="h-3.5 w-3.5 animate-pulse rounded bg-gray-200" />
              <div
                className="h-3 animate-pulse rounded bg-gray-200"
                style={{ width: `${60 + ((i * 17) % 40)}%` }}
              />
              <div className="ml-auto h-3 w-8 animate-pulse rounded bg-gray-200" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  5. StoreTabSkeleton — Product header + retailer comparison table  */
/* ------------------------------------------------------------------ */

export function StoreTabSkeleton() {
  return (
    <div aria-hidden="true">
      {/* Product info header */}
      <div className="mb-4">
        {/* Part # */}
        <div className="flex items-center gap-2">
          <div className="h-4 w-12 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-28 animate-pulse rounded bg-gray-200" />
        </div>
        {/* Brand */}
        <div className="mt-1 flex items-center gap-2">
          <div className="h-4 w-12 animate-pulse rounded bg-gray-200" />
          <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
        </div>
        {/* Title */}
        <div className="mt-2 h-4 w-3/4 animate-pulse rounded bg-gray-200" />
        {/* Category */}
        <div className="mt-1 h-3 w-1/2 animate-pulse rounded bg-gray-200" />
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              {["Store", "Price", "Stock", "Reviews", "Rating"].map((col) => (
                <th
                  key={col}
                  className="px-3 py-2 text-left text-xs font-semibold text-gray-400"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 5 }, (_, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td className="px-3 py-2">
                  <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                </td>
                <td className="px-3 py-2">
                  <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                </td>
                <td className="px-3 py-2">
                  <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
                </td>
                <td className="px-3 py-2">
                  <div className="h-4 w-12 animate-pulse rounded bg-gray-200" />
                </td>
                <td className="px-3 py-2">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }, (_, j) => (
                      <div key={j} className="h-3 w-3 animate-pulse rounded bg-gray-200" />
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  6. PicsTabSkeleton — grid of 70x70 image placeholders             */
/* ------------------------------------------------------------------ */

export function PicsTabSkeleton() {
  return (
    <div
      className="grid grid-cols-3 gap-4 sm:grid-cols-4 md:grid-cols-5"
      aria-hidden="true"
    >
      {Array.from({ length: 10 }, (_, i) => (
        <div key={i} className="flex flex-col items-center">
          <div className="h-[70px] w-[70px] animate-pulse rounded bg-gray-200" />
          <div className="mt-1 h-3 w-14 animate-pulse rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  7. FitsTabSkeleton — total count + data table (5 cols x 6 rows)   */
/* ------------------------------------------------------------------ */

export function FitsTabSkeleton() {
  return (
    <div aria-hidden="true">
      {/* Total count */}
      <div className="mb-2 flex items-center gap-1">
        <div className="h-3 w-10 animate-pulse rounded bg-gray-200" />
        <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-200 bg-gray-50">
            <tr>
              {Array.from({ length: 5 }, (_, i) => (
                <th key={i} className="px-3 py-2">
                  <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 6 }, (_, i) => (
              <tr key={i} className="border-b border-gray-100">
                {Array.from({ length: 5 }, (_, j) => (
                  <td key={j} className="whitespace-nowrap px-3 py-2">
                    <div
                      className="h-3 animate-pulse rounded bg-gray-200"
                      style={{ width: `${48 + ((j * 13 + i * 7) % 32)}px` }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  8. SpecsTabSkeleton — 2-column table (Attribute | Value)          */
/* ------------------------------------------------------------------ */

export function SpecsTabSkeleton() {
  return (
    <div className="overflow-x-auto" aria-hidden="true">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400">
              Attribute
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400">
              Value
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 8 }, (_, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="px-3 py-2">
                <div
                  className="h-3 animate-pulse rounded bg-gray-200"
                  style={{ width: `${80 + ((i * 11) % 40)}px` }}
                />
              </td>
              <td className="px-3 py-2">
                <div
                  className="h-3 animate-pulse rounded bg-gray-200"
                  style={{ width: `${60 + ((i * 17) % 60)}px` }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  9. XRefTabSkeleton — 2-column table (Part Number | Brand)         */
/* ------------------------------------------------------------------ */

export function XRefTabSkeleton() {
  return (
    <div className="overflow-x-auto" aria-hidden="true">
      <table className="w-full text-sm">
        <thead className="border-b border-gray-200 bg-gray-50">
          <tr>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400">
              Part Number
            </th>
            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-400">
              Brand
            </th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="px-3 py-2">
                <div
                  className="h-3 animate-pulse rounded bg-gray-200"
                  style={{ width: `${70 + ((i * 13) % 30)}px` }}
                />
              </td>
              <td className="px-3 py-2">
                <div
                  className="h-3 animate-pulse rounded bg-gray-200"
                  style={{ width: `${60 + ((i * 11) % 40)}px` }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 10. SearchAutocompleteSkeleton — 5 text rows in dropdown           */
/* ------------------------------------------------------------------ */

export function SearchAutocompleteSkeleton() {
  return (
    <div
      className="absolute top-full left-0 z-50 mt-0.5 w-full rounded-b border border-gray-200 bg-white shadow-lg"
      aria-hidden="true"
    >
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={i}
          className="border-b border-gray-100 px-4 py-2.5 last:border-b-0"
        >
          <div
            className="h-4 animate-pulse rounded bg-gray-200"
            style={{ width: `${65 + ((i * 19) % 30)}%` }}
          />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 11. VehicleFilterSkeleton — 3 dropdown placeholders                */
/* ------------------------------------------------------------------ */

export function VehicleFilterSkeleton() {
  return (
    <div className="flex flex-col gap-3" aria-hidden="true">
      {["Year", "Make", "Model"].map((label) => (
        <div key={label}>
          <div className="mb-1 h-3 w-10 animate-pulse rounded bg-gray-200" />
          <div className="h-7 w-full animate-pulse rounded border border-gray-200 bg-gray-100" />
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 12. AttributesGroupSkeleton — loading state inside expanded group  */
/* ------------------------------------------------------------------ */

export function AttributesGroupSkeleton() {
  return (
    <div className="space-y-1 pb-2" aria-hidden="true">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="flex items-center gap-2 py-0.5">
          <div className="h-3.5 w-3.5 animate-pulse rounded bg-gray-200" />
          <div
            className="h-3 flex-1 animate-pulse rounded bg-gray-200"
            style={{ maxWidth: `${50 + ((i * 15) % 40)}%` }}
          />
          <div className="h-3 w-8 animate-pulse rounded bg-gray-200" />
        </div>
      ))}
    </div>
  );
}
